import gsap from 'gsap';

const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const canHover = window.matchMedia('(hover: hover)').matches;
const mm = gsap.matchMedia();

const asArray = (target) => {
  if (!target) {
    return [];
  }

  if (Array.isArray(target)) {
    return target.filter(Boolean);
  }

  if (typeof NodeList !== 'undefined' && target instanceof NodeList) {
    return Array.from(target).filter(Boolean);
  }

  if (typeof HTMLCollection !== 'undefined' && target instanceof HTMLCollection) {
    return Array.from(target).filter(Boolean);
  }

  return [target].filter(Boolean);
};

const setTransitionNone = (target) => {
  const elements = asArray(target);
  elements.forEach((el) => {
    if (!el) {
      return;
    }

    if (el.dataset.motionOriginalTransition === undefined) {
      el.dataset.motionOriginalTransition = el.style.transition || '';
    }

    el.style.transition = 'none';
  });
};

const restoreTransition = (target) => {
  const elements = asArray(target);
  elements.forEach((el) => {
    if (!el) {
      return;
    }

    if (el.dataset.motionOriginalTransition !== undefined) {
      el.style.transition = el.dataset.motionOriginalTransition;
      delete el.dataset.motionOriginalTransition;
    } else {
      el.style.transition = '';
    }
  });
};

const setWillChange = (targets, props) => {
  const elements = asArray(targets);
  if (!elements.length) {
    return;
  }

  const value = Array.isArray(props) ? props.join(', ') : props;

  elements.forEach((el) => {
    if (!el) {
      return;
    }

    if (el.dataset.motionOriginalWillChange === undefined) {
      el.dataset.motionOriginalWillChange = el.style.willChange || '';
    }

    el.style.willChange = value;
  });
};

const clearWillChange = (targets) => {
  const elements = asArray(targets);
  if (!elements.length) {
    return;
  }

  elements.forEach((el) => {
    if (!el) {
      return;
    }

    if (el.dataset.motionOriginalWillChange !== undefined) {
      el.style.willChange = el.dataset.motionOriginalWillChange;
      delete el.dataset.motionOriginalWillChange;
    } else {
      el.style.willChange = '';
    }
  });
};

const revealText = (target, opts = {}) => {
  const elements = asArray(target);
  if (!elements.length) {
    return gsap.timeline();
  }

  const {
    duration = 0.92,
    y = 18,
    stagger = 0,
    delay = 0,
    ease = 'power3.out',
  } = opts;

  const tl = gsap.timeline();
  const from = { y, opacity: 0 };
  const to = { y: 0, opacity: 1 };

  if (!prefersReduced) {
    from.clipPath = 'inset(0% 0% 100% 0%)';
    to.clipPath = 'none';
  }

  setWillChange(elements, ['transform', 'opacity', 'clip-path']);

  tl.fromTo(
    elements,
    from,
    {
      ...to,
      duration,
      delay,
      ease,
      stagger,
    }
  );

  tl.call(() => clearWillChange(elements));

  return tl;
};

const revealGroup = (targets, opts = {}) => {
  const {
    duration = 0.84,
    y = 24,
    stagger = 0.08,
    ease = 'power3.out',
    delay = 0,
    ...rest
  } = opts;

  return revealText(targets, { duration, y, stagger, ease, delay, ...rest });
};

const setupMagneticButton = (el) => {
  if (!el || prefersReduced || !canHover || el.dataset.motionMagnetic === 'bound') {
    return;
  }

  const strength = 6;

  const pointerMove = (event) => {
    const bounds = el.getBoundingClientRect();
    const percentX = (event.clientX - (bounds.left + bounds.width / 2)) / (bounds.width / 2);
    const percentY = (event.clientY - (bounds.top + bounds.height / 2)) / (bounds.height / 2);
    const x = gsap.utils.clamp(-1, 1, percentX) * strength;
    const y = gsap.utils.clamp(-1, 1, percentY) * (strength * 0.6);

    gsap.to(el, { x, y, duration: 0.45, ease: 'power3.out' });
  };

  const resetTransform = () => {
    gsap.to(el, { x: 0, y: 0, duration: 0.4, ease: 'expo.out' });
  };

  const press = () => {
    gsap.to(el, { scale: 0.98, duration: 0.18, ease: 'power3.out' });
  };

  const release = () => {
    gsap.to(el, { scale: 1, duration: 0.35, ease: 'power3.out' });
  };

  el.addEventListener('mousemove', pointerMove);
  el.addEventListener('mouseleave', () => {
    resetTransform();
    release();
  });
  el.addEventListener('pointerdown', press);
  el.addEventListener('pointerup', release);
  el.addEventListener('pointerleave', release);

  el.dataset.motionMagnetic = 'bound';
};

const getScrollerElement = (ctx) => {
  return ctx.lenisInstance?.getScrollerElement?.() || document.scrollingElement || document.documentElement;
};

export function initScrollAnimations(ctx) {
  const scroller = getScrollerElement(ctx);
  if (!scroller) {
    return ctx;
  }

  mm.add(
    {
      isDesktop: '(min-width: 960px)',
    },
    (context) => {
      const { isDesktop } = context.conditions;
      const motionScale = isDesktop ? 1 : 0.7;
      const detailY = isDesktop ? 24 : 16;
      const textY = isDesktop ? 18 : 12;
      const mediaY = isDesktop ? 26 : 18;
      const trackY = isDesktop ? 30 : 22;
      const controlsY = isDesktop ? 28 : 22;
      const durations = {
        segment: 0.7 * motionScale,
        text: 0.62 * motionScale,
        media: 0.5 * motionScale,
        cta: 0.65 * motionScale,
        progress: 0.7 * motionScale,
        track: 0.45 * motionScale,
      };
      const blogDurations = {
        track: 0.45 * motionScale,
        slides: 0.5 * motionScale,
        nav: 0.4 * motionScale,
      };
      const ease = 'power3.out';

      const detailTrigger = ctx.detailSection;
      const hasDetailContent = detailTrigger && (ctx.detailSegments.length || ctx.detailTitle || ctx.detailDescription);
      const clearDetailSegmentInlineStyles = () => {
        if (!ctx.detailSegments.length) {
          return;
        }

        gsap.set(ctx.detailSegments, { clearProps: 'transform,scale,opacity' });
      };
      const detailTimeline = hasDetailContent
        ? gsap.timeline({
          scrollTrigger: {
            trigger: detailTrigger,
            scroller,
            start: 'top 80%',
            toggleActions: 'play reverse play reverse',
            invalidateOnRefresh: true,
            onLeave: clearDetailSegmentInlineStyles,
            onLeaveBack: clearDetailSegmentInlineStyles,
          },
        })
        : null;

      if (detailTimeline) {
        detailTimeline.addLabel('detail-start');

        if (ctx.detailSegments.length) {
          detailTimeline.call(() => setWillChange(ctx.detailSegments, ['transform', 'opacity']), null, 'detail-start');
          detailTimeline.from(
            ctx.detailSegments,
            {
              y: detailY,
              opacity: 0,
              scale: 0.99,
              duration: durations.segment,
              ease,
              stagger: 0.08,
            },
            'detail-start'
          );
          detailTimeline.to(
            ctx.detailSegments,
            {
              y: -4,
              duration: 0.35 * motionScale,
              ease,
              stagger: 0.05,
            },
            'detail-start+=0.55'
          );
          detailTimeline.to(
            ctx.detailSegments,
            {
              y: 0,
              duration: 0.4 * motionScale,
              ease,
              stagger: 0.05,
            },
            '<'
          );

          // Run cleanup only after the final staggered segment settles.
          detailTimeline.call(() => clearWillChange(ctx.detailSegments), null, '>');
          detailTimeline.call(clearDetailSegmentInlineStyles, null, '>');
        }

        const detailTextTargets = [ctx.detailTitle, ctx.detailDescription].filter(Boolean);
        if (detailTextTargets.length) {
          detailTimeline.add(
            revealText(detailTextTargets, {
              duration: durations.text,
              y: textY,
              stagger: 0.05,
            }),
            ctx.detailSegments.length ? 'detail-start+=0.4' : 'detail-start'
          );
        }
      }

      const ecommerceTrigger = ctx.ecommerceSection;
      const hasEcommerceTargets = ecommerceTrigger && (ctx.ecommerceMedia || ctx.ecommerceText || ctx.ecommerceCTA || ctx.ecommerceProgressTrack);
      const ecommerceTimeline = hasEcommerceTargets
        ? gsap.timeline({
          scrollTrigger: {
            trigger: ecommerceTrigger,
            scroller,
            start: 'top 85%',
            toggleActions: 'play reverse play reverse',
            invalidateOnRefresh: true,
          },
        })
        : null;

      if (ecommerceTimeline) {
        if (ctx.ecommerceMedia) {
          ecommerceTimeline.call(() => setWillChange(ctx.ecommerceMedia, ['transform', 'opacity']), null, 0);
          ecommerceTimeline.from(
            ctx.ecommerceMedia,
            {
              y: mediaY,
              opacity: 0,
              scale: 1.01,
              duration: durations.media,
              ease,
            },
            0
          );
          ecommerceTimeline.call(() => clearWillChange(ctx.ecommerceMedia), null, '+=0');
        }

        if (ctx.ecommerceText) {
          ecommerceTimeline.add(
            revealText(ctx.ecommerceText, {
              duration: durations.text,
              y: textY,
              stagger: 0,
            }),
            ctx.ecommerceMedia ? '-=0.65' : 0
          );
        }

        if (ctx.ecommerceProgressTrack && !ctx.ecommerceScrollTimeline) {
          ecommerceTimeline.call(() => setWillChange(ctx.ecommerceProgressTrack, 'transform'), null, ctx.ecommerceText ? '-=0.5' : 0);
          ecommerceTimeline.from(
            ctx.ecommerceProgressTrack,
            {
              scaleX: 0,
              transformOrigin: 'left center',
              duration: durations.progress,
              ease: 'expo.out',
            },
            ctx.ecommerceText ? '-=0.75' : '-=0.4'
          );
          ecommerceTimeline.call(() => clearWillChange(ctx.ecommerceProgressTrack), null, '+=0');
        }

        if (ctx.ecommerceCTA) {
          const ctaTimeline = gsap.timeline();
          ctaTimeline.call(() => setWillChange(ctx.ecommerceCTA, ['transform', 'opacity']), null, 0);
          ctaTimeline.from(
            ctx.ecommerceCTA,
            {
              y: 10,
              opacity: 0,
              scale: prefersReduced ? 1 : 0.96,
              duration: durations.cta,
              ease,
            },
            0
          );
          ctaTimeline.call(() => clearWillChange(ctx.ecommerceCTA), null, '+=0');
          ecommerceTimeline.add(ctaTimeline, ctx.ecommerceProgressTrack ? '-=0.45' : '-=0.4');
          ecommerceTimeline.call(() => setupMagneticButton(ctx.ecommerceCTA), null, '<');
        }
      }

      const blogTrigger = ctx.blogSliderSection;
      const hasBlogTargets = blogTrigger && (ctx.blogSliderTitle || ctx.blogSliderNav || ctx.blogSliderTrack);
      const blogTimeline = hasBlogTargets
        ? gsap.timeline({
          scrollTrigger: {
            trigger: blogTrigger,
            scroller,
            start: 'top 190%',
            toggleActions: 'play reverse play reverse',
            invalidateOnRefresh: true,
          },
        })
        : null;

      const blogSlides = ctx.blogSliderTrack
        ? asArray(ctx.blogSliderTrack.querySelectorAll('.keen-slider__slide'))
        : [];
      const blogNavIcons = ctx.blogSliderNav
        ? asArray(ctx.blogSliderNav.querySelectorAll('.home-blog__nav-icon'))
        : [];

      // Wrapper para animar sem tocar no transform do Keen (track)
      const blogMotionEl =
        ctx.blogInner ||
        ctx.blogSliderTrack?.closest('.home-blog') ||
        ctx.blogSliderTrack?.parentElement;

      // Pre-set para evitar blink (executa imediatamente, não depende do scroll)
      if (ctx.blogSliderTrack && ctx.blogSliderTrack.dataset.motionPreset !== '1') {
        ctx.blogSliderTrack.dataset.motionPreset = '1';
        // Pre-set SEM tocar no transform do Keen
        if (blogMotionEl && blogMotionEl.dataset.motionPreset !== '1') {
          blogMotionEl.dataset.motionPreset = '1';
          gsap.set(blogMotionEl, { opacity: 0, y: trackY });
        }

        if (blogSlides.length && ctx.blogSliderTrack?.dataset.motionSlidesPreset !== '1') {
          ctx.blogSliderTrack.dataset.motionSlidesPreset = '1';
          gsap.set(blogSlides, { opacity: 0, y: Math.max(14, trackY * 0.45) });
        }
      }

      if (blogSlides.length && (!ctx.blogSliderTrack || ctx.blogSliderTrack.dataset.motionSlidesPreset !== '1')) {
        if (ctx.blogSliderTrack) ctx.blogSliderTrack.dataset.motionSlidesPreset = '1';
        gsap.set(blogSlides, { opacity: 0, y: Math.max(14, trackY * 0.45), scale: 1 });
      }

      if (blogNavIcons.length && (!ctx.blogSliderNav || ctx.blogSliderNav.dataset.motionNavPreset !== '1')) {
        if (ctx.blogSliderNav) ctx.blogSliderNav.dataset.motionNavPreset = '1';
        gsap.set(blogNavIcons, { opacity: 0, x: 8 });
      }


      if (blogTimeline) {
        if (ctx.blogInner) {
          blogTimeline.call(() => setWillChange(ctx.blogInner, ['transform', 'opacity']), null, 0);
          blogTimeline.from(
            ctx.blogInner,
            {
              y: textY * 1.2,
              opacity: 0,
              duration: durations.text + 0.1,
              ease,
            },
            0
          );
          blogTimeline.call(() => clearWillChange(ctx.blogInner), null, '+=0');
        }

        if (ctx.blogSliderTitle) {
          blogTimeline.add(revealText(ctx.blogSliderTitle, { duration: durations.text, y: textY }), 0);
        }

        if (ctx.blogSliderNav) {
          const navChildren = asArray(ctx.blogSliderNav.children);
          const navTargets = navChildren.length ? navChildren : [ctx.blogSliderNav];
          blogTimeline.add(
            revealGroup(navTargets, { duration: durations.segment, y: textY, stagger: 0.08 }),
            ctx.blogSliderTitle ? '-=0.5' : 0
          );
        }

        if (ctx.blogSliderTrack) {
          blogTimeline.addLabel('blog-track');

          blogTimeline.call(() => {
            setWillChange(ctx.blogSliderTrack, ['transform', 'opacity']);
            if (blogSlides.length) setWillChange(blogSlides, ['transform', 'opacity']);
          }, null, 'blog-track');

          // Track: glide + settle (sem blur, sem piscar)
          if (ctx.blogSliderTrack && blogMotionEl) {
            blogTimeline.addLabel('blog-track');

            blogTimeline.call(() => {
              // Track: só opacity (não mexer em transform)
              setWillChange(ctx.blogSliderTrack, 'opacity');

              // Wrapper e slides podem animar transform sem conflitar com Keen
              setWillChange(blogMotionEl, ['transform', 'opacity']);
              if (blogSlides.length) setWillChange(blogSlides, ['transform', 'opacity']);
            }, null, 'blog-track');

            // Garante que o track aparece, mas sem tocar em transform
            blogTimeline.to(
              ctx.blogSliderTrack,
              { opacity: 1, duration: 0.2 * motionScale, ease: 'power1.out' },
              'blog-track'
            );

            // Animação fluida no WRAPPER (não no track)
            blogTimeline.to(
              blogMotionEl,
              {
                keyframes: [
                  { opacity: 1, y: 0, duration: 0.58 * motionScale, ease: 'expo.out' },
                  { y: -3 * motionScale, duration: 0.18 * motionScale, ease: 'power2.out' },
                  { y: 0, duration: 0.26 * motionScale, ease: 'power3.out' },
                ],
              },
              'blog-track'
            );

            // Slides (ok animar y neles)
            if (blogSlides.length) {
              blogTimeline.to(
                blogSlides,
                {
                  opacity: 1,
                  y: 0,
                  duration: 0.62 * motionScale,
                  ease: 'expo.out',
                  stagger: { each: 0.06 * motionScale, from: 'start' },
                  overwrite: 'auto',
                },
                'blog-track+=0.10'
              );
            }

            blogTimeline.call(() => {
              clearWillChange(ctx.blogSliderTrack);
              clearWillChange(blogMotionEl);
              if (blogSlides.length) clearWillChange(blogSlides);

              // limpa estilos inline sem encostar no transform do track
              gsap.set(blogMotionEl, { clearProps: 'transform,opacity,willChange' });
              gsap.set(ctx.blogSliderTrack, { clearProps: 'willChange,opacity' });
            }, null, 'blog-track+=1.05');
          }

          // Cards: entra suave, com stagger curto e “assentada” leve
          if (blogSlides.length) {
            blogTimeline.to(
              blogSlides,
              {
                opacity: 1,
                y: 0,
                scale: 1,
                duration: 0.62 * motionScale,
                ease: 'expo.out',
                stagger: { each: 0.06 * motionScale, from: 'start' },
                overwrite: 'auto',
              },
              'blog-track+=0.10'
            );

            blogTimeline.to(
              blogSlides,
              {
                y: (i) => (i % 3 === 0 ? -1.5 : -1) * motionScale,
                duration: 0.16 * motionScale,
                ease: 'power2.out',
                stagger: { each: 0.03 * motionScale, from: 'start' },
              },
              'blog-track+=0.62'
            );

            blogTimeline.to(
              blogSlides,
              {
                y: 0,
                duration: 0.22 * motionScale,
                ease: 'power3.out',
                stagger: { each: 0.03 * motionScale, from: 'start' },
              },
              'blog-track+=0.78'
            );
          }

          blogTimeline.call(() => {
            clearWillChange(ctx.blogSliderTrack);
            if (blogSlides.length) clearWillChange(blogSlides);

            // garante que não fica preso em estilos inline
            gsap.set(ctx.blogSliderTrack, { clearProps: 'willChange' });
            if (blogSlides.length) gsap.set(blogSlides, { clearProps: 'willChange' });
          }, null, 'blog-track+=1.05');
        }


        if (blogNavIcons.length) {
          blogTimeline.to(
            blogNavIcons,
            {
              opacity: 1,
              x: 0,
              duration: 0.42 * motionScale,
              ease: 'power3.out',
              stagger: 0.06 * motionScale,
              overwrite: 'auto',
            },
            'blog-track+=0.25'
          );

          blogTimeline.call(() => {
            gsap.set(blogNavIcons, { clearProps: 'opacity,transform' });
          }, null, 'blog-track+=1.05');
        }

      }

      const ecoTrigger = ctx.ecoSliderSection;
      const hasEcoTargets = ecoTrigger && (ctx.ecoSliderTrack || ctx.ecoSliderControls);
      const ecoTimeline = hasEcoTargets
        ? gsap.timeline({
          scrollTrigger: {
            trigger: ecoTrigger,
            scroller,
            start: 'top 90%',
            toggleActions: 'play reverse play reverse',
            invalidateOnRefresh: true,
          },
        })
        : null;

      if (ecoTimeline) {
        if (ctx.ecoSliderTrack) {
          ecoTimeline.call(() => setWillChange(ctx.ecoSliderTrack, ['transform', 'opacity']), null, 0);
          ecoTimeline.from(
            ctx.ecoSliderTrack,
            {
              y: 34,
              opacity: 0,
              scale: 1.01,
              duration: durations.track,
              ease,
            },
            0
          );
          ecoTimeline.call(() => clearWillChange(ctx.ecoSliderTrack), null, '+=0');
        }

        if (ctx.ecoSliderControls) {
          const controlChildren = asArray(ctx.ecoSliderControls.children);
          if (controlChildren.length) {
            ecoTimeline.add(
              revealGroup(controlChildren, { duration: durations.segment, y: controlsY, stagger: 0.08 }),
              '-=0.5'
            );
          }

          const arrowIcons = asArray(ctx.ecoSliderControls.querySelectorAll('.home-eco-slider__nav-icon'));
          if (arrowIcons.length) {
            ecoTimeline.from(
              arrowIcons,
              {
                y: 18,
                opacity: 0,
                duration: 0.72 * motionScale,
                ease,
                stagger: 0.1,
              },
              '-=0.45'
            );
          }
        }
      }

      const historyTrigger = ctx.historySection;
      const hasHistoryTargets = historyTrigger && (ctx.historyTitle || ctx.historyDescription || ctx.historyCTA || ctx.historyAsideText);
      const historyTimeline = hasHistoryTargets
        ? gsap.timeline({
          scrollTrigger: {
            trigger: historyTrigger,
            scroller,
            start: 'top 85%',
            toggleActions: 'play reverse play reverse',
            invalidateOnRefresh: true,
          },
        })
        : null;

      if (historyTimeline) {
        const historyTextTargets = [ctx.historyTitle, ctx.historyDescription, ctx.historyAsideText].filter(Boolean);
        if (historyTextTargets.length) {
          historyTimeline.add(
            revealText(historyTextTargets, { duration: durations.text, y: textY, stagger: 0.05 }),
            0
          );
        }

        if (ctx.historyCTA) {
          historyTimeline.call(() => setWillChange(ctx.historyCTA, ['transform', 'opacity']), null, '-=0.4');
          historyTimeline.from(
            ctx.historyCTA,
            {
              y: 12,
              opacity: 0,
              scale: prefersReduced ? 1 : 0.96,
              duration: durations.cta,
              ease,
            },
            ctx.historyCTA ? '-=0.25' : 0
          );
          historyTimeline.call(() => clearWillChange(ctx.historyCTA), null, '+=0');
        }
      }

      return () => {
        detailTimeline?.kill();
        ecommerceTimeline?.kill();
        blogTimeline?.kill();
        ecoTimeline?.kill();
        historyTimeline?.kill();
      };
    }
  );

  return ctx;
}
